/**
 * KAN-964: общая сетка месяца (вынесена из KAN-758 CrmRangeCalendar): шапка со
 * стрелками месяцев, строка дней недели с понедельника и клетки дней. Что рисовать
 * в клетке дня (кнопка выбора, отметка) решает вызывающий через renderDay.
 */
import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { MONTHS_NOMINATIVE, WEEKDAYS_SHORT, monthGrid, shiftMonth, type MonthRef } from '@/lib/crmRange';

interface MonthCalendarProps {
  month: MonthRef;
  onMonthChange: (month: MonthRef) => void;
  renderDay: (day: string) => ReactNode;
  /** false - стрелка вперёд недоступна (будущие месяцы не нужны). */
  canGoNext?: boolean;
  testId?: string;
  monthTestId?: string;
  /** Классы ячейки заголовка дня недели (ширина колонки). */
  weekdayClassName?: string;
}

const NAV_BTN = 'flex h-7 w-7 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 disabled:opacity-30';

export function MonthCalendar({
  month,
  onMonthChange,
  renderDay,
  canGoNext = true,
  testId,
  monthTestId,
  weekdayClassName = 'h-6 w-8',
}: MonthCalendarProps) {
  return (
    <div data-testid={testId}>
      <div className="mb-2 flex items-center justify-between">
        <button type="button" aria-label="Предыдущий месяц" className={NAV_BTN} onClick={() => onMonthChange(shiftMonth(month, -1))}>
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-medium text-gray-900" data-testid={monthTestId}>
          {MONTHS_NOMINATIVE[month.month - 1]} {month.year}
        </span>
        <button
          type="button"
          aria-label="Следующий месяц"
          className={NAV_BTN}
          disabled={!canGoNext}
          onClick={() => onMonthChange(shiftMonth(month, 1))}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <table className="border-collapse">
        <thead>
          <tr>
            {WEEKDAYS_SHORT.map((d) => (
              <th key={d} className={`${weekdayClassName} text-center text-xs font-normal text-gray-400`}>
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {monthGrid(month).map((week, i) => (
            <tr key={i}>
              {week.map((day, j) =>
                day ? (
                  <td key={day} className="p-0.5 text-center">
                    {renderDay(day)}
                  </td>
                ) : (
                  <td key={j} />
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
